import type { DraftReview, Problem, ReviewFinding } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Table } from '../components/Table';
import { findingSeverityView, reviewStageLabel } from '../app/status';
import { formatDateTime } from '../lib/datetime';

/** 指摘の移動先。label はボタンの名前（「要素へ」「ファイルへ」）。 */
export interface FindingTarget {
  label: string;
  onOpen(): void;
}

export interface ReviewPanelProps {
  review: DraftReview;
  validating: boolean;
  validateProblem: Problem | null;
  onValidate(): void;
  /** null なら移動のボタンを出さない。 */
  targetOf(finding: ReviewFinding): FindingTarget | null;
  /** あるときだけ「AI に直させる」を出す（Studio だけ。D30）。 */
  onAskFix?(finding: ReviewFinding): void;
}

const COLUMNS = [
  { key: 'severity', header: '重大度' },
  { key: 'target', header: '対象' },
  { key: 'message', header: '理由' },
  { key: 'actions', header: '操作' },
];

/**
 * 検証レポートの指摘（Claude Design の REVIEW）。Studio のレビュー表示と microapp 詳細のレビュータブで共有する。
 * 表示するのは head と digest が一致するレポート、無ければ前回のレポート（そのときは「まだ検証していない」と出す）。
 */
export function ReviewPanel({ review, validating, validateProblem, onValidate, targetOf, onAskFix }: ReviewPanelProps) {
  const report = review.report ?? review.latestReport;

  return (
    <section className="review-panel" aria-label="レビュー">
      <div className="review-head">
        <h2 className="review-title">レビュー</h2>
        {report !== null && (
          <>
            <Badge tone="failed">{`エラー ${report.errorCount}`}</Badge>
            <Badge tone="unknown">{`警告 ${report.warningCount}`}</Badge>
          </>
        )}
        {review.report !== null && (
          <span className="hint">{`最終検証 ${formatDateTime(review.report.createdAt)} · 版 ${review.headRevisionId.slice(0, 8)}`}</span>
        )}
        <span className="spacer" />
        <Button disabled={validating} onClick={onValidate}>
          {validating ? '検証中…' : '検証する'}
        </Button>
      </div>
      <p className="hint">エラーが残っていると「試す」を押せません。警告は試せますが、公開前に解消を推奨します。</p>
      <ProblemNotice problem={validateProblem} />
      {review.report === null && review.latestReport !== null && (
        <p className="problem problem-notice review-stale" role="status">
          {`現在の版はまだ検証していません（前回の検証 ${formatDateTime(review.latestReport.createdAt)}）`}
        </p>
      )}
      {report === null ? (
        <EmptyState
          title="まだ検証していません"
          description="「検証する」で manifest・BPMN・DMN・設定・DDL の検査と品質レビューを行います。"
        />
      ) : report.findings.length === 0 ? (
        <EmptyState title="指摘はありません" />
      ) : (
        <>
          {report.truncated && <p className="hint">指摘が多いため先頭 200 件だけを表示しています</p>}
          <Table
            label="指摘"
            columns={COLUMNS}
            rows={report.findings.map((finding, index) => {
              const severity = findingSeverityView(finding.severity);
              const target = targetOf(finding);
              const stage = reviewStageLabel(finding.stage);
              return {
                key: `${index}:${finding.code}`,
                cells: {
                  severity: <Badge tone={severity.tone}>{severity.label}</Badge>,
                  target: (
                    <span className="review-target">
                      <code>{finding.elementId ?? finding.path ?? '—'}</code>
                      <span className="hint">
                        {finding.elementId !== null && finding.path !== null ? `${stage} · ${finding.path}` : stage}
                      </span>
                    </span>
                  ),
                  message: <span className="review-message">{finding.message}</span>,
                  actions: (
                    <span className="review-actions">
                      {target !== null && <Button onClick={target.onOpen}>{target.label}</Button>}
                      {onAskFix !== undefined && <Button onClick={() => onAskFix(finding)}>AI に直させる</Button>}
                    </span>
                  ),
                },
              };
            })}
          />
        </>
      )}
      <p className="review-note">出典不明・未確定は指摘に含めません。業務フローのバッジで確認してください。</p>
    </section>
  );
}
